import { ReactNode, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Plus, FileText } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { handleError } from "@/lib/errorHandler";
import { useSchemaDetection } from "@/hooks/useSchemaDetection";
import { StructurePreviewDrawer } from "@/components/ai/StructurePreviewDrawer";

interface SmartTableCreatorProps {
  baseId: string;
  onTableCreated: () => void;
  children?: ReactNode;
}

export const SmartTableCreator = ({ baseId, onTableCreated, children }: SmartTableCreatorProps) => {
  const [open, setOpen] = useState(false);
  const [tableName, setTableName] = useState("");
  const [tableDescription, setTableDescription] = useState("");
  const [pastedContent, setPastedContent] = useState("");
  const [creating, setCreating] = useState(false);
  const { isDetecting, detectedSchema, detectSchema, clearSchema } = useSchemaDetection();
  const [previewOpen, setPreviewOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleManualCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);

    try {
      const { error } = await supabase
        .from("tables")
        .insert([{
          base_id: baseId,
          name: tableName,
          description: tableDescription
        }]);

      if (error) throw error;

      toast.success("Table created!");
      resetForm();
      onTableCreated();
    } catch (error: any) {
      handleError(error, "Failed to create table");
    } finally {
      setCreating(false);
    }
  };

  const handlePaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const text = e.clipboardData.getData("text");
    if (text && text.length > 20) {
      // Detect if it looks like CSV or structured data
      const hasCommas = text.includes(",");
      const hasMultipleLines = text.split("\n").length > 2;

      if (hasCommas || hasMultipleLines) {
        toast.info("Analyzing pasted content...");
        await detectSchema(text, "table_creation");
        if (detectedSchema) {
          setPreviewOpen(true);
        }
      }
    }
  };

  const handleSmartCreate = async (schema: any) => {
    setCreating(true);

    try {
      // Create table
      const { data: table, error: tableError } = await supabase
        .from("tables")
        .insert([{
          base_id: baseId,
          name: schema.tableName,
          description: `Auto-created from content analysis`
        }])
        .select()
        .single();

      if (tableError) throw tableError;

      // Create fields
      for (let i = 0; i < schema.fields.length; i++) {
        const field = schema.fields[i];
        const { error: fieldError } = await supabase
          .from("fields")
          .insert([{
            table_id: table.id,
            name: field.name,
            type: field.type,
            order_index: i,
            is_required: field.isRequired || false,
            config: field.config || {}
          }]);

        if (fieldError) throw fieldError;
      }

      // Create sample records if available
      if (schema.records && schema.records.length > 0) {
        for (const record of schema.records) {
          await supabase
            .from("records")
            .insert([{
              table_id: table.id,
              data: record,
              created_by: (await supabase.auth.getUser()).data.user!.id
            }]);
        }
      }

      toast.success(`Table "${schema.tableName}" created with ${schema.fields.length} fields!`);
      resetForm();
      onTableCreated();
      setPreviewOpen(false);
    } catch (error: any) {
      handleError(error, "Failed to create table from schema");
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setTableName("");
    setTableDescription("");
    setPastedContent("");
    clearSchema();
    setOpen(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) resetForm();
      }}>
        <DialogTrigger asChild>
          {children || (
            <Button size="sm" variant="ghost">
              <Plus className="h-4 w-4" />
            </Button>
          )}
        </DialogTrigger>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Table</DialogTitle>
            <DialogDescription>
              Create manually or paste CSV/text to auto-detect structure
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="smart" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="smart">
                <FileText className="h-4 w-4 mr-2" />
                Smart Create
              </TabsTrigger>
              <TabsTrigger value="manual">Manual</TabsTrigger>
            </TabsList>

            <TabsContent value="smart" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="paste-content">Paste CSV or Text Content</Label>
                <Textarea
                  ref={textareaRef}
                  id="paste-content"
                  placeholder="Paste CSV data, spreadsheet content, or any structured text here..."
                  value={pastedContent}
                  onChange={(e) => setPastedContent(e.target.value)}
                  onPaste={handlePaste}
                  rows={8}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  AI will automatically detect the structure and suggest fields
                </p>
              </div>

              <Button
                onClick={() => detectSchema(pastedContent, "table_creation").then(() => setPreviewOpen(true))}
                disabled={isDetecting || !pastedContent.trim()}
                className="w-full"
              >
                {isDetecting ? "Analyzing..." : "Analyze Content"}
              </Button>
            </TabsContent>

            <TabsContent value="manual">
              <form onSubmit={handleManualCreate} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="table-name">Table Name</Label>
                  <Input
                    id="table-name"
                    placeholder="e.g., Customers, Orders, Tasks"
                    value={tableName}
                    onChange={(e) => setTableName(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="table-description">Description (Optional)</Label>
                  <Textarea
                    id="table-description"
                    placeholder="What this table is for..."
                    value={tableDescription}
                    onChange={(e) => setTableDescription(e.target.value)}
                    rows={3}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={creating}>
                  {creating ? "Creating..." : "Create Table"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <StructurePreviewDrawer
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        schema={detectedSchema}
        onAccept={handleSmartCreate}
        onReject={() => {
          setPreviewOpen(false);
          clearSchema();
        }}
      />
    </>
  );
};
